import type { EncounterSpecialty } from "@/api/encounters";
import type { CodeItem } from "@/ai-coding-outputs/admin-coding/codeComparison";
import { ROUTES } from "@/ai-coding-outputs/navigation";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/user/useUserProfiles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import CodeReviewColumn from "./CodeReviewColumn";
import { useCreateHumanEncounterReview } from "./useCreateHumanEncounterReview";
import { useWriteBackActions } from "./useWriteBackActions";
import { useWriteBackResult } from "./useWriteBackResult";

export interface HumanReviewColumnProps {
  reviewId: string;
  encounterId: string;
  aiCodesForComparison: {
    procedures: CodeItem[];
    diagnoses: CodeItem[];
  };
  onSubmitSuccess: () => void;
  customer: { label: string };
  /** Called when admin requests to void this review (voids immediately; no confirmation). */
  onRequestVoidReview?: (reviewId: string) => void;
  /** Called when admin requests to unvoid this review (opens confirmation in parent). */
  onRequestUnvoidReview?: (reviewId: string) => void;
  /** Whether the current user can void/unvoid reviews (admin or clinical_coder_admin). */
  canVoidUnvoidReview: boolean;
  /** Whether this review is voided (excluded from write-back). */
  isVoided: boolean;
  opcsLabel?: string;
  isGoldenDatasetSupported: boolean;
  specialty?: EncounterSpecialty | null;
  patientAge?: number | null;
  patientSex?: string | null;
  historicCodes?: ReadonlySet<string>;
  primaryDiagnosisIdentified?: boolean | null;
}

const HumanReviewColumn = memo(
  ({
    reviewId,
    encounterId,
    customer,
    aiCodesForComparison,
    onSubmitSuccess,
    onRequestVoidReview,
    onRequestUnvoidReview,
    canVoidUnvoidReview,
    isVoided,
    opcsLabel,
    isGoldenDatasetSupported,
    specialty,
    patientAge,
    patientSex,
    historicCodes,
    primaryDiagnosisIdentified,
  }: HumanReviewColumnProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { createReview } = useCreateHumanEncounterReview();
    const { writeBackHumanReviewMutation } = useWriteBackActions();

    const { data: humanReview, isLoading: isLoadingHumanReview } = useQuery({
      queryKey: ["human-review", reviewId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("encounter_human_review")
          .select(
            `*,
            procedures:encounter_human_review_procedure_code(*),
            diagnoses:encounter_human_review_diagnosis_code(*),
            hrg:hrgs(*)
            `,
          )
          .eq("id", reviewId)
          .single();
        if (error) throw error;
        if (!data) throw new Error("Human review not found");
        return {
          ...data,
          procedures: data.procedures
            .slice()
            .sort((a, b) => a.order_index - b.order_index)
            .map((procedure) => ({
              id: procedure.id,
              code: procedure.code,
              description: procedure.description,
              orderIndex: procedure.order_index,
            })),
          diagnoses: data.diagnoses
            .slice()
            .sort((a, b) => a.order_index - b.order_index)
            .map((diagnosis) => ({
              id: diagnosis.id,
              code: diagnosis.code,
              description: diagnosis.description,
              orderIndex: diagnosis.order_index,
            })),
        };
      },
    });

    const { data: writeBackResult } = useWriteBackResult(reviewId, encounterId);
    const latestWriteBack = writeBackResult?.latest ?? null;
    const isWrittenBack = writeBackResult?.isWrittenBack ?? false;
    const isSuperseded = writeBackResult?.isSuperseded ?? false;
    const currentWriteBackStatus = writeBackResult?.head?.status ?? null;

    const { data: reviewerProfileData, isLoading: isLoadingReviewerProfile } = useUserProfile(humanReview?.reviewer_id);
    const reviewerProfile = humanReview?.reviewer_id ? reviewerProfileData?.[humanReview.reviewer_id] : undefined;

    // Wrapper to call the mutation with the human review id
    const writeBackResultMutation = useMutation({
      mutationFn: async () => {
        if (!humanReview) return;
        if (isVoided) return;
        await writeBackHumanReviewMutation.mutateAsync({
          encounter_id: humanReview.encounter_id,
          human_review_id: reviewId,
        });
      },
      onSuccess: () => {
        onSubmitSuccess();
      },
      onError: (error) => {
        console.error("Failed to write back result:", error);
        toast.error(`Failed to write back result: ${error.message}`);
      },
    });

    const linkOutToReviewMutation = useMutation({
      mutationFn: async () => {
        if (!humanReview) return;
        try {
          await createReview({
            encounterId: humanReview.encounter_id,
            procedures: humanReview.procedures,
            diagnoses: humanReview.diagnoses,
          });
          navigate(`${ROUTES.humanCodingReview.route}/${humanReview.encounter_id}?fromAdminReview=true`);
        } catch (error) {
          // Error handling is managed by the hook (toast notifications)
          console.error("Failed to create review:", error);
        }
      },
      onError: (error) => {
        console.error("Failed to link out to review:", error);
        toast.error(`Failed to link out to review: ${error.message}`);
      },
    });

    if (isLoadingHumanReview || isLoadingReviewerProfile) {
      return null;
    }

    if (!humanReview) return null;

    const status = isSuperseded ? "superseded" : (latestWriteBack?.status ?? "pending");
    const statusClass =
      status === "success"
        ? "border-green-300 bg-green-50 text-green-700"
        : status === "error"
          ? "border-red-300 bg-red-50 text-red-700"
          : "border-gray-300 bg-gray-50 text-gray-700";

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`inline-flex w-full min-h-[32px] items-center rounded-md border px-2 py-1 text-xs shadow-sm ${statusClass}`}
          >
            {latestWriteBack?.status ? `${status} (write back)` : "Not written back"}
          </span>
        </div>
        <CodeReviewColumn
          encounterId={encounterId}
          isWrittenBack={isWrittenBack}
          isSuperseded={isSuperseded}
          currentWriteBackStatus={currentWriteBackStatus}
          reviewerName={reviewerProfile?.display_name ?? "Unknown Reviewer"}
          reviewerColor={reviewerProfile?.display_color || "#6366f1"}
          procedures={humanReview.procedures}
          diagnoses={humanReview.diagnoses}
          notes={humanReview.notes}
          hrgCode={humanReview.hrg?.code}
          customerLabel={customer.label}
          aiCodesForComparison={aiCodesForComparison}
          outcome={humanReview.outcome}
          writeBackResultMutation={writeBackResultMutation}
          linkOutToReviewMutation={linkOutToReviewMutation}
          onVoidReview={canVoidUnvoidReview && !isVoided ? () => onRequestVoidReview?.(reviewId) : undefined}
          onUnvoidReview={canVoidUnvoidReview && isVoided ? () => onRequestUnvoidReview?.(reviewId) : undefined}
          showVoidUnvoidButton={canVoidUnvoidReview}
          isVoided={isVoided}
          opcsLabel={opcsLabel}
          goldenDatasetSource={{ kind: "human_review", id: reviewId }}
          isGoldenDatasetSupported={isGoldenDatasetSupported}
          onGoldenDatasetCodesChanged={() => queryClient.invalidateQueries({ queryKey: ["human-review", reviewId] })}
          specialty={specialty}
          patientAge={patientAge}
          patientSex={patientSex}
          historicCodes={historicCodes}
          primaryDiagnosisIdentified={primaryDiagnosisIdentified}
        />
      </div>
    );
  },
);

HumanReviewColumn.displayName = "HumanReviewColumn";

export default HumanReviewColumn;
